import React, { useState } from 'react';
import {
  intl,
  DmsButton,
  fetchData,
  HTTP_STATUS,
} from "dmsbasereact";
import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    TextField,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export const GenerateApiKeyModal = ({ open, onClose, onSuccess }) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedKey, setGeneratedKey] = useState(null);

    const handleGenerate = async () => {
        setIsGenerating(true);
        try {
            // Use fetchData with show_feedback=true to automatically show success/error messages
            const response = await fetchData('/api_key/generate', 'POST', true, {}, false, true);

            if (response.status === HTTP_STATUS.OK_200 || response.status === HTTP_STATUS.CREATED_201) {
                setGeneratedKey(response.data.key);
            }
        } catch (err) {
            console.error('Failed to generate API key:', err);
            onClose();
        } finally {
            setIsGenerating(false);
        }
    };

    const handleClose = () => {
        if (generatedKey && onSuccess) {
            onSuccess();
        }
        setGeneratedKey(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={generatedKey ? handleClose : onClose}
            fullWidth
            maxWidth="sm"
            disableEscapeKeyDown={!!generatedKey}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VpnKeyIcon />
                {intl.get("GenerateAPIKey").d("Generate API Key")}
            </DialogTitle>
            <DialogContent>
                {!generatedKey ? (
                    <Box sx={{ mt: 2 }}>
                        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {intl.get("ImportantWarning").d("⚠️ IMPORTANT WARNING")}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {intl.get("ApiKeyWarning1").d("The API key will be shown ONLY ONCE after generation.")}
                            </Typography>
                            <Typography variant="body2">
                                {intl.get("ApiKeyWarning2").d("You must copy and save it immediately in a secure location.")}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                {intl.get("ApiKeyWarning3").d("If you lose the key, you will need to contact an administrator to reset it.")}
                            </Typography>
                        </Alert>

                        <Typography variant="body1">
                            {intl.get("GenerateApiKeyQuestion").d("Do you want to generate your API key now?")}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ mt: 2 }}>
                        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {intl.get("ApiKeyGenerated").d("✓ API Key Generated Successfully")}
                            </Typography>
                        </Alert>

                        <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {intl.get("SaveNow").d("⚠️ SAVE THIS KEY NOW!")}
                            </Typography>
                            <Typography variant="body2">
                                {intl.get("KeyNeverShownAgain").d("This key will NEVER be shown again. Copy it to a secure location immediately.")}
                            </Typography>
                        </Alert>

                        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                            {intl.get("SelectAndCopyKey").d("Select and copy the key below:")}
                        </Typography>

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={generatedKey}
                            InputProps={{
                                readOnly: true,
                            }}
                            sx={{
                                '& .MuiInputBase-root': {
                                    fontFamily: 'monospace',
                                    fontSize: '0.9rem',
                                    backgroundColor: '#f5f5f5',
                                },
                                '& .MuiInputBase-input': {
                                    cursor: 'text',
                                    userSelect: 'all',
                                }
                            }}
                            onClick={(e) => e.target.select()}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {!generatedKey ? (
                    <>
                        <DmsButton
                            onClick={onClose}
                            text={intl.get("Cancel").d("Cancel")}
                            disabled={isGenerating}
                        />
                        <DmsButton
                            onClick={handleGenerate}
                            variant="primary"
                            text={intl.get("Generate").d("Generate")}
                            disabled={isGenerating}
                            startIcon={<VpnKeyIcon />}
                        />
                    </>
                ) : (
                    <DmsButton
                        onClick={handleClose}
                        variant="primary"
                        text={intl.get("IHaveSavedTheKey").d("I have saved the key")}
                    />
                )}
            </DialogActions>
        </Dialog>
    );
};
